import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../../types/notification';
import { formatDistanceToNow } from 'date-fns';
import Avatar from '../UI/Avatar';
import IconSVG from '../UI/IconSVG';
import { 
  handleNotificationClick, 
  getNotificationIcon, 
  getNotificationTitle, 
  getNotificationBody 
} from '../../utils/notification-navigation';
import { useAppSelector } from '../../store/hooks';
import { getProfileByUid } from '../../services/firebase/profile.service';
import logger from '../../utils/logger';

interface LegacyNotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}

const LegacyNotificationItem: React.FC<LegacyNotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
}) => {
  const navigate = useNavigate();
  const [initiatorProfile, setInitiatorProfile] = useState<any>(null);
  
  // Get friends profiles from store
  const friendsProfiles = useAppSelector(state => state.user.friendsMap);
  const viewedProfiles = useAppSelector(state => state.user.viewedProfiles);
  
  // Extract the needed properties
  const { id, type } = notification;
  const dateCreated = (notification as any).timestamp || notification.dateCreated;
  const isRead = (notification as any).isRead || notification.read;
  const eventInitiator = (notification as any).eventInitiator;
  
  // Load initiator profile if needed
  useEffect(() => {
    if (eventInitiator && !notification.isSysGen) {
      // Check if we already have the profile
      const existingProfile = friendsProfiles[eventInitiator] || viewedProfiles[eventInitiator];
      if (existingProfile) {
        setInitiatorProfile(existingProfile);
      } else {
        // Fetch the profile
        getProfileByUid(eventInitiator).then(profile => {
          if (profile) {
            setInitiatorProfile(profile);
          }
        }).catch(error => {
          logger.error('Error fetching notification initiator profile:', error);
        });
      }
    }
  }, [eventInitiator, friendsProfiles, viewedProfiles, notification.isSysGen]);
  
  // Get title and body using helpers or from legacy fields
  const title = getNotificationTitle(notification);
  const body = getNotificationBody(notification);
  const message = (notification as any).message;

  // Format the time distance
  const timeAgo = formatDistanceToNow(dateCreated || Date.now(), { addSuffix: true });

  // Get the notification background color based on read status
  const getBackgroundColor = () => {
    if (!isRead) {
      return 'bg-blue-50 dark:bg-blue-900/20';
    }
    return 'bg-white dark:bg-gray-800';
  };

  // Handle notification click
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Mark as read using the correct field
    if (!isRead) {
      onMarkAsRead(id);
    }
    
    handleNotificationClick(notification, navigate);
  };

  // Handle notification delete
  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(id);
  };

  const icon = getNotificationIcon(type);
  const bgColor = getBackgroundColor();
  
  // For system notifications, show Marune logo
  if (notification.isSysGen) {
    return (
      <div
        className={`block border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer ${bgColor}`}
        onClick={handleClick}
      >
        <div className="flex p-4">
          {/* Marune logo for system notifications */}
          <div className="flex-shrink-0 mr-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-purple-100 dark:bg-purple-800">
              <IconSVG icon="bell" className="text-purple-500 dark:text-purple-400" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {message || title || 'System Notification'}
              </h4>
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                {timeAgo}
              </span>
            </div>
            
            {body && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                {body}
              </p>
            )}

            {/* System badge */}
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-800/20 dark:text-purple-300">
                System
              </span>
            </div>
          </div>

          {/* Delete button */}
          <button
            onClick={handleDelete}
            className="ml-4 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            aria-label="Delete notification"
          >
            <IconSVG icon="close" className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>
    );
  }

  // For regular notifications
  return (
    <div
      className={`block border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer ${bgColor}`}
      onClick={handleClick}
    >
      <div className="flex p-4">
        {/* User avatar or icon */}
        <div className="flex-shrink-0 mr-4">
          {initiatorProfile?.profilePhotoURL ? (
            <div className="relative">
              <Avatar 
                src={initiatorProfile.profilePhotoURL} 
                alt={initiatorProfile.username || 'User'} 
                size="md" 
              />
              {/* Notification type icon overlay */}
              <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                notification.isAdditional
                  ? 'bg-green-500'
                  : type === 'requestReceived' || type === 'requestAccepted'
                  ? 'bg-blue-500'
                  : type === 'oss'
                  ? 'bg-red-500'
                  : type === 'comment' || type === 'tag'
                  ? 'bg-orange-500'
                  : 'bg-gray-500'
              }`}>
                <IconSVG icon={icon} className="w-3 h-3 text-white" />
              </div>
            </div>
          ) : (
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              notification.isAdditional
                ? 'bg-green-100 dark:bg-green-800'
                : 'bg-blue-100 dark:bg-blue-800'
            }`}>
              <IconSVG 
                icon={icon} 
                className={
                  notification.isAdditional
                    ? 'text-green-500 dark:text-green-400'
                    : 'text-blue-500 dark:text-blue-400'
                } 
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div className="flex-1 mr-2">
              {initiatorProfile && (
                <span className="font-medium text-gray-900 dark:text-white">
                  {initiatorProfile.username || initiatorProfile.displayName || 'User'}
                </span>
              )}
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {' '}{message || title || body || 'sent you a notification'}
              </span>
              {('eventReference' in notification) && notification.eventReference?.postTitle && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 italic">
                  "{notification.eventReference.postTitle}"
                </p>
              )}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {timeAgo}
            </span>
          </div>
          
          {/* Additional notification badge */}
          {notification.isAdditional && (
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-300">
                Announcement
              </span>
            </div>
          )}
        </div>

        {/* Delete button */}
        <button
          onClick={handleDelete}
          className="ml-4 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          aria-label="Delete notification"
        >
          <IconSVG icon="close" className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </button>
      </div>
    </div>
  );
};

export default LegacyNotificationItem;